import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { Display, setDisplay, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER_COLUMN, FLEX_ITEM, JUSTIFY_CONTENT } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { anyReallyAny } from "@cervval.js/common/tools";
import { Picker, PickerOptions } from "../picker";
import { I18nType, i18n } from "./i18n/i18n";

/** Options for FilePicker */
export interface FilePickerOptions extends PickerOptions {

    /** Title */
    pickerTitle: string;

    /** Font Awesome icon in drag&Drop zone*/
    icon: string;

    /** 
     * Files type accepted by input
     * @see https://developer.mozilla.org/fr/docs/Web/HTML/Element/Input/file#identifiants_de_type_de_fichier
     */
    accepts: string[];

    /** Enable multiple file pick */
    enableMultiple: boolean;

    /** Max file size accepted by filePicker (in MB)*/
    maxFileSize: number;

    /** Display options config of filePicker (as a string in component)*/
    displayOptions: boolean;
}

/** Options for FilePicker */
export const defaultFilePickerOptions: FilePickerOptions = {

    /** Title */
    pickerTitle: "Upload files",

    /** Font Awesome icon in drag&Drop zone*/
    icon: "fa-file-arrow-up",

    /** 
     * Files type accepted by input
     * @see https://developer.mozilla.org/fr/docs/Web/HTML/Element/Input/file#identifiants_de_type_de_fichier
     */
    accepts: ["*"],

    /** Enable multiple file pick */
    enableMultiple: true,

    /** Max file size accepted by filePicker (in MB)*/
    maxFileSize: -1,

    /** Display options config of filePicker (as a string in component)*/
    displayOptions: false,

    /** Disabled filePicker */
    disabled: false
};

@CustomElement({
    name: "cjs-file-picker",
    style: require("./filePicker.scss"),
    template: require("./filePicker.html"),
    i18n,
    elementCssClasses: [FLEX_CONTAINER_COLUMN, FLEX_ITEM, JUSTIFY_CONTENT.CENTER, "form-control"]
})
export class CjsFilePicker<I18n extends I18nType = I18nType> extends Picker<File[], I18n> {

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    @TypedWebComponentOptions(defaultFilePickerOptions)
    public override readonly options!: WebComponentOptions<FilePickerOptions>;

    /** list of files identified */
    protected override _value: File[] = [];

    @TypedHtmlElements()
    public readonly components!: {
        filePickerTitle: HTMLParagraphElement,
        filePickerIcon: HTMLElement,
        filePickerInput: HTMLInputElement,
        filePickerOptions: HTMLDivElement,
        filePickerMultipleError: HTMLParagraphElement,
        filePickerSizeError: HTMLParagraphElement,
        filePickerExtensionError: HTMLParagraphElement
    };

    /** Message shown in the console on multiple file error */
    protected _multipleErrorMessage!: string;

    /** Message shown on file size error */
    protected _fileSizeErrorMessage!: string;

    /** Message shown on accept option */
    protected _acceptOption!: string;

    /** Message shown on file extension error */
    protected _fileExtensionErrorMessage!: string;

    /** Message shown on file size option */
    protected _sizeLimitOption!: string;

    constructor(options?: Partial<FilePickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /**
     * When component is init
     */
    public override init(): void {

        this.components.filePickerMultipleError.innerHTML = this.i18n.multipleError.innerHTML;
        this._multipleErrorMessage = this.i18n.multipleError.innerHTML;
        this._fileSizeErrorMessage = this.i18n.maxSizeError.text;
        this._fileExtensionErrorMessage = this.i18n.fileExtensionError.text;
        this._acceptOption = this.i18n.accept.text;
        this._sizeLimitOption = this.i18n.sizeLimit.text;
        if (!this.disabled) {
            this.tabIndex = 0;
        }

        // prevent default
        ["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
            this.addEventListener(eventName, this._preventDefaults, false);
        });

        // highlight on dragover
        ["dragenter", "dragover"].forEach(eventName => {
            this.addEventListener(eventName, this._highlight, false);
        });

        // unhighlight on stop drag or on drop
        ["dragleave", "drop"].forEach(eventName => {
            this.addEventListener(eventName, this._unhighlight, false);
        });

        // drop file event
        this.addEventListener("drop", this._handleDrop, false);
        // click input event simulation
        this.addEventListener("click", this.inputFileClick, false);
        this.addEventListener("keydown", (event) => {
            if (event.key === "Enter") {
                this.inputFileClick(event);
            }
        });
        // click input change file
        this.components.filePickerInput.addEventListener("change", this._handleSelectFiles, false);
    }


    /** Refresh component display */
    public override refresh(): void {
        super.refresh();
        this._value = [];

        setDisplay(this.components.filePickerMultipleError, Display.NONE);
        this.components.filePickerInput.setAttribute("multiple", `${this.options.enableMultiple}`);
        this.components.filePickerInput.setAttribute("accept", this._getAcceptsString());
        this.components.filePickerIcon.className = `fa-solid ${this.options.icon} fa-4x`;
        this.components.filePickerTitle.innerHTML = this.options.pickerTitle;

        toggleVisible(this.components.filePickerOptions, this.options.displayOptions, Display.FLEX);
        this.components.filePickerOptions.innerHTML = this._getOptionsString();
    }

    /**
     * Public function to get files to upload
     * @returns list of files
     */
    public getFiles(): File[] {
        return this._value;
    }

    /**
     * Set the picker files list
     * @param files 
     */
    public setFiles(files: File[]): void {
        this._value = files;
    }

    /**
     * Empty file picker files list
     * @param p object
     * @param p.doNotDispatchEvent true to don't trigger valueChange event
     */
    public emptyFiles(p?: { doNotDispatchEvent?: boolean }): void {
        if (!this._value.length) { return; }// Already empty

        this.setFiles([]);// Clear value
        this.components.filePickerInput.value = "";// Remove files in input
        if (!p?.doNotDispatchEvent) {
            this._dispatchOnPickerValueChange();
        }
    }

    /**
     * simulate input click
     */
    public inputFileClick = (e: Event): void => {
        if (this.disabled) { return; }// Don't allow input click callback if component is disabled

        this.components.filePickerInput.click();
    };

    /**
     * Handle action when dropping files
     */
    private _handleDrop = (e: anyReallyAny): void => {
        if (this.disabled) { return; }// Don't allow drop callback if component is disabled

        setDisplay(this.components.filePickerSizeError, Display.NONE);
        setDisplay(this.components.filePickerExtensionError, Display.NONE);

        const files: File[] = Array.from(e.dataTransfer.files);
        if (!this.options.enableMultiple && files.length > 1) {
            /** Exception when no multiple file authorized */
            this.components.filePickerMultipleError.style.display = "inherit";
            this._log?.error(this._multipleErrorMessage);
            return;
        }
        this._value = [];

        // Hide error containers
        setDisplay(this.components.filePickerSizeError, Display.NONE);
        setDisplay(this.components.filePickerMultipleError, Display.NONE);
        setDisplay(this.components.filePickerExtensionError, Display.NONE);

        // Check size
        for (const file of files) {
            if (!this._handleFileSize(file)) {
                setDisplay(this.components.filePickerSizeError, Display.FLEX);
                this.components.filePickerSizeError.innerHTML = this._fileSizeErrorMessage + this.options.maxFileSize + " " + this.i18n.sizeUnit.text;

                return;
            }

            if (!this._handleFileExtension(file)) {
                setDisplay(this.components.filePickerExtensionError, Display.FLEX);
                this.components.filePickerExtensionError.innerHTML = this._fileExtensionErrorMessage;

                return;
            }
        }

        if (this.options.enableMultiple) { // Add file to file list
            this._value = [...this._value, ...files];
        } else { // Set file to file list
            this._value = [...files]; // only one file given (checked before)
        }
        this._dispatchOnPickerValueChange();
    };

    /**
     * action when select a files
     */
    private _handleSelectFiles = (e: Event): void => {
        // Ensure component isn't disabled before selecting files (user can still remove css to re-enable it)
        if (this.disabled) { return; }

        const input = this.components.filePickerInput;
        if (input.files == null || !input.files?.length) { return; }

        const files: File[] = Array.from(input.files);
        if (!this.options.enableMultiple && files.length > 1) {
            /** Exception when no multiple file authorized */
            this.components.filePickerMultipleError.style.display = "inherit";
            this._log?.error(this._multipleErrorMessage);
            return;
        }
        this._value = [];

        // Hide error containers
        setDisplay(this.components.filePickerSizeError, Display.NONE);
        setDisplay(this.components.filePickerMultipleError, Display.NONE);
        setDisplay(this.components.filePickerExtensionError, Display.NONE);

        for (const file of files) {
            if (!this._handleFileSize(file)) {
                setDisplay(this.components.filePickerSizeError, Display.FLEX);
                this.components.filePickerSizeError.innerHTML = this._fileSizeErrorMessage + this.options.maxFileSize + " " + this.i18n.sizeUnit.text;
                return;
            }
            if (!this._handleFileExtension(file)) {
                setDisplay(this.components.filePickerExtensionError, Display.FLEX);
                this.components.filePickerExtensionError.innerHTML = this._fileExtensionErrorMessage;

                return;
            }
        }

        // Update files
        if (this.options.enableMultiple) { // Add file to file list
            this._value = [...this._value, ...files];
        } else { // Set file to file list
            this._value = [...files]; // only one file given (checked before)
        }
        this._dispatchOnPickerValueChange();
    };

    /**
     * Prevent default events
     */
    private _preventDefaults(e: Event): void {
        e.preventDefault();
        e.stopPropagation();
    }

    /**
     * Highlight to display dropzone
     */
    private _highlight(e: Event): void {
        if (this.disabled) { return; }// Don't allow highlight callback if component is disabled

        this.classList.add("highlight");
    }

    /**
     * Unhighlight when out of dropzone
     */
    private _unhighlight(e: Event): void {
        if (this.disabled) { return; }// Don't allow unHighlight callback if component is disabled

        this.classList.remove("highlight");
    }

    /** 
     * Handle file size check
     * @returns true if file size is valid, else: false
     */
    private _handleFileSize(file: File): boolean {

        if (this.options.maxFileSize < 0) { // No file size check asked in options (default value: -1)
            return true;
        } else { // Check file size under authorized one
            return this.options.maxFileSize > file.size / 1024 / 1024;
        }
    }


    /** 
     * Handle file size check
     * @returns true if file size is valid, else: false
     */
    private _handleFileExtension(file: File): boolean {

        const fileType = file.type.toLocaleLowerCase();
        if (this.options.accepts.length && this.options.accepts[0] === defaultFilePickerOptions.accepts[0]) { // No file extension check asked in options (default value: ["*"]) 
            return true;
        } else if (this.options.accepts.includes(fileType)) {
            return true;
        } else {
            for (const accept of this.options.accepts) {
                const lastChar = accept.substring(accept.length - 1);
                if (lastChar === "*") {
                    const genericType = accept.replace(lastChar, "");
                    if (fileType.includes(genericType)) {
                        return true;
                    }
                } else {
                    // If MIME checks failed. Try to check the extension only
                    const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLocaleLowerCase();
                    return this.options.accepts.includes(fileExtension);
                }
            }
        }
        return false;
    }

    /**
     * Get accepts attribute has valid string format
     * @see https://developer.mozilla.org/fr/docs/Web/HTML/Element/Input/file#attr-accept
     */
    private _getAcceptsString(): string {
        return this.options.accepts.join(",");
    }

    /**
     * Get options string to display in DOM
     */
    protected _getOptionsString(): string {
        return `${this.options.accepts.length > 0 ? this.i18n.accept.text + "(" + this.options.accepts.join(", ") + ")" : ""} ${this.options.maxFileSize > 0 ? this.i18n.sizeLimit.text + " - " + this.options.maxFileSize + this.i18n.sizeUnit.text : ""}`;
    }

}